// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2ForYou.test.tsx
import { type AskPunttForYouWaitingItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Button, Dialog, Flex, Heading, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  formatListCardRelativeDate,
  LIST_CARD_RELATIVE_TIME_CLASS,
} from "../../components/ask-puntt-list/listCardTimestamps";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  KNOWLEDGE_DATABASE_PATH,
} from "../../routes/settings/routes/knowledge-database/search";
import { AskV2ConflictCard } from "../project/AskV2ConflictCard";
import { openItemRowTitle } from "../project/openItemRowTitle";

function iso(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : value;
}

/** The row's title for a waiting item: the same short line the project rail shows. */
export function waitingItemTitle(item: AskPunttForYouWaitingItem): string {
  return openItemRowTitle({
    id: item.id,
    kind: item.kind,
    needed: item.needed,
    ownerUserId: null,
    openedAt: item.openedAt,
    fileId: null,
    sourceId: item.id,
    sourceKind: "agent",
  });
}

/** "Raised by Name (Role)", "Raised by Name", or null when nobody raised it. */
export function useRaisedByLine(
  item: AskPunttForYouWaitingItem,
): string | null {
  const { t } = useTranslation("ui");
  if (!item.raisedByName) return null;
  return item.raisedByRole
    ? t("askV2.home.forYou.raisedBy", {
        name: item.raisedByName,
        role: item.raisedByRole,
      })
    : t("askV2.home.forYou.raisedByNoRole", { name: item.raisedByName });
}

/** A request opens its own panel: one with Accept / Keep, or one from a chat outside any project. */
export function opensRequestPanel(item: AskPunttForYouWaitingItem): boolean {
  return (item.resolutionOptions?.length ?? 0) > 0 || item.projectId == null;
}

/**
 * One request waiting on the viewer, answered on its own (decision 12, 21 Oct):
 * what is asked, who asked and when, the sentence it came from, what the
 * knowledge already says, and its Accept / Keep choices with a reason. It never
 * links to the chat the request came from; a project the viewer may read keeps
 * a link.
 */
export function AskV2RequestPanel({
  item,
  viewerIsExpert,
  onClose,
  onOpenProject,
}: {
  item: AskPunttForYouWaitingItem;
  viewerIsExpert: boolean;
  onClose: () => void;
  onOpenProject: (item: AskPunttForYouWaitingItem) => void;
}) {
  const { t } = useTranslation("ui");
  const raisedBy = useRaisedByLine(item);
  const age = formatListCardRelativeDate(iso(item.openedAt));
  const saidTitleId = `ask-v2-request-panel-said-title-${item.id}`;
  const evidenceTitleId = `ask-v2-request-panel-evidence-title-${item.id}`;
  const projectLinked = item.projectId != null && item.projectVisible !== false;
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Content
        maxWidth="560px"
        data-testid="ask-v2-request-panel"
        aria-roledescription={t("askV2.home.forYou.panel.label")}
      >
        <Dialog.Title size="4" data-testid="ask-v2-request-panel-title">
          {waitingItemTitle(item)}
        </Dialog.Title>
        <Dialog.Description
          size="2"
          data-testid="ask-v2-request-panel-needed"
          className="text-puntt-neutral-gray-12"
        >
          {item.needed}
        </Dialog.Description>
        <Text
          as="p"
          size="1"
          data-testid="ask-v2-request-panel-raised"
          className="mt-2 flex flex-wrap items-center gap-2 text-puntt-neutral-gray-11"
        >
          {raisedBy ? <span>{raisedBy}</span> : null}
          {age ? (
            <span className={LIST_CARD_RELATIVE_TIME_CLASS}>{age}</span>
          ) : null}
        </Text>
        {item.statement ? (
          <section
            aria-labelledby={saidTitleId}
            data-testid="ask-v2-request-panel-statement"
            className="mt-3 flex flex-col gap-1"
          >
            <Heading
              as="h3"
              size="2"
              weight="medium"
              id={saidTitleId}
              className="text-puntt-neutral-gray-11"
            >
              {t("askV2.home.forYou.panel.saidTitle")}
            </Heading>
            <blockquote className="border-l-2 border-puntt-neutral-gray-6 pl-3 text-[13px] text-puntt-neutral-gray-12">
              {item.statement}
            </blockquote>
          </section>
        ) : null}
        {item.rule ? (
          <section
            aria-labelledby={evidenceTitleId}
            data-testid="ask-v2-request-panel-rule"
            className="mt-3 flex flex-col gap-1"
          >
            <Heading
              as="h3"
              size="2"
              weight="medium"
              id={evidenceTitleId}
              className="text-puntt-neutral-gray-11"
            >
              {t("askV2.home.forYou.panel.evidenceTitle")}
            </Heading>
            <blockquote className="border-l-2 border-puntt-neutral-gray-6 pl-3 text-[13px] text-puntt-neutral-gray-12">
              {item.rule.text}
            </blockquote>
            {item.rule.sourceName ? (
              <Text
                as="p"
                size="1"
                data-testid="ask-v2-request-panel-rule-source"
                className="text-puntt-neutral-gray-11"
              >
                {t("askV2.home.forYou.panel.ruleSource", {
                  source: item.rule.sourceName,
                })}
              </Text>
            ) : null}
            <Link
              to={KNOWLEDGE_DATABASE_PATH}
              search={{
                ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                tab: "rules",
                ruleId: item.rule.id,
              }}
              target="_blank"
              data-testid="ask-v2-request-panel-rule-link"
              className="text-[13px] font-medium text-puntt-blue-11 underline"
            >
              {t("askV2.home.forYou.panel.openRule")}
            </Link>
          </section>
        ) : item.evidence ? (
          <section
            aria-labelledby={evidenceTitleId}
            data-testid="ask-v2-request-panel-evidence"
            className="mt-3 flex flex-col gap-1"
          >
            <Heading
              as="h3"
              size="2"
              weight="medium"
              id={evidenceTitleId}
              className="text-puntt-neutral-gray-11"
            >
              {t("askV2.home.forYou.panel.evidenceTitle")}
            </Heading>
            <Text as="p" size="2" className="text-puntt-neutral-gray-12">
              {item.evidence}
            </Text>
          </section>
        ) : null}
        {item.resolutionOptions && item.resolutionOptions.length > 0 ? (
          <div className="mt-3">
            <AskV2ConflictCard
              projectId={item.projectId}
              itemId={item.id}
              options={item.resolutionOptions}
              canAct={viewerIsExpert}
              reasonRequired={!item.statement}
              ruleReview={Boolean(item.rule)}
            />
          </div>
        ) : null}
        <Flex gap="2" mt="3" justify="end">
          {projectLinked ? (
            <Button
              type="button"
              variant="soft"
              data-testid="ask-v2-request-panel-open-project"
              onClick={() => onOpenProject(item)}
            >
              {t("askV2.home.forYou.panel.openProject")}
            </Button>
          ) : null}
          <Dialog.Close>
            <Button
              type="button"
              variant="soft"
              color="gray"
              data-testid="ask-v2-request-panel-close"
            >
              {t("askV2.home.forYou.panel.close")}
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
